import { Box, Tooltip, useTheme } from '@material-ui/core';
import {
  DATE_FORMAT,
  FilterOperator,
  formatDate,
  formatDateReadable,
  formatDateTimeReadable,
} from '@nemuru-eng/js-api-client';
import React from 'react';
import { useForm } from 'react-hook-form';

import { ListFilter } from '@/components/modules/CheckoutSession/List/types/ListFilter';
import { CustomInputDate } from '@/components/shared/components/CustomInputDate';
import { Text } from '@/components/shared/components/Text';
import { CustomPalette } from '@/config/types';
import { useConfigContext } from '@/contexts/ConfigContext/ConfigContext';
import { adjustDateTimeByTimezoneOffset, setDateTimeToZero } from '@/utils/timeFormatters';
interface DateFilterFormValues {
  fromDate: Date | undefined;
  toDate: Date | undefined;
}

export const useDateFilters = ({
  defaultValues,
  dateField = 'created_at',
  showSearch = true,
}: {
  defaultValues?: { fromDate?: Date; toDate?: Date };
  dateField?: string;
  showSearch?: boolean;
}) => {
  const {
    state: { dictionaryValues },
  } = useConfigContext();
  const {
    watch,
    setValue,
    formState: { errors },
  } = useForm<DateFilterFormValues>({
    defaultValues: {
      fromDate: defaultValues?.fromDate,
      toDate: defaultValues?.toDate,
    },
  });
  const fromDateValue = watch('fromDate');
  const toDateValue = watch('toDate');
  const toDate = toDateValue && new Date(toDateValue.toString());
  const dateFilters = [] as ListFilter[];

  const { palette, shape }: { palette: CustomPalette; shape } = useTheme();

  const defaultWrapperStyle = {
    borderRadius: shape.borderRadius,
    padding: !showSearch ? '0px 8px 5px 4px' : '0px 0 5px 4px',
    marginBottom: '10px',
  } as React.CSSProperties;
  const activeWrapperStyle = {
    ...defaultWrapperStyle,
    background: palette.grey[100],
  } as React.CSSProperties;

  const isValidDate = (date) => {
    if (!isNaN(date) && date instanceof Date) return true;
  };

  const dateFiltersContent = (
    <Box display="inline-flex" gridGap={12}>
      <Tooltip
        title={`${dictionaryValues['FILTER_FROM']} ${dictionaryValues[dateField]}${
          fromDateValue && isValidDate(fromDateValue) ? `: ${formatDateTimeReadable(fromDateValue)}` : ''
        }`}
        placement={'bottom-start'}
      >
        <Box style={fromDateValue ? activeWrapperStyle : defaultWrapperStyle}>
          <CustomInputDate
            name="fromDate"
            field={{
              value: fromDateValue ?? null,
              onChange: (e) => {
                setValue('fromDate', e ?? undefined);
              },
            }}
            style={{ top: '-20px', maxHeight: '30px' }}
            locale={dictionaryValues['DATE_LOCALE']}
            fullWidth
            views={['year', 'month', 'date']}
            format="dd/MM/yyyy"
            label={<Text tId="FROM_DATE" />}
            maxDate={toDate}
            allowKeyboardPicking
            disableFuture={true}
            error={errors.fromDate}
          />
        </Box>
      </Tooltip>
      <Tooltip
        title={`${dictionaryValues['FILTER_TO']} ${dictionaryValues[dateField]}${
          toDate && isValidDate(toDate) ? `: ${formatDateReadable(toDate)}` : ''
        }`}
        placement={'bottom-start'}
      >
        <Box style={toDate ? activeWrapperStyle : defaultWrapperStyle}>
          <CustomInputDate
            name="toDate"
            field={{
              value: toDateValue ?? null,
              onChange: (e) => {
                setValue('toDate', e ?? undefined);
              },
            }}
            style={{ top: '-20px', maxHeight: '30px' }}
            locale={dictionaryValues['DATE_LOCALE']}
            fullWidth
            views={['year', 'month', 'date']}
            format="dd/MM/yyyy"
            label={<Text tId="TO_DATE" />}
            minDate={fromDateValue}
            allowKeyboardPicking
            disableFuture={true}
            error={errors.toDate}
          />
        </Box>
      </Tooltip>
    </Box>
  );

  if (fromDateValue)
    dateFilters.push({
      field: dateField,
      operator: FilterOperator.GTE,
      value: [adjustDateTimeByTimezoneOffset(setDateTimeToZero(fromDateValue))],
    });
  if (toDate) {
    const toDatePlusDayOffset = formatDate(new Date(toDate.setDate(toDate.getDate() + 1)), DATE_FORMAT.ATOM);
    dateFilters.push({
      field: dateField,
      operator: FilterOperator.LTE,
      value: [adjustDateTimeByTimezoneOffset(setDateTimeToZero(new Date(toDatePlusDayOffset)))],
    });
  }

  const resetDateFilters = () => {
    setValue('fromDate', undefined);
    setValue('toDate', undefined);
  };

  return {
    dateFilters,
    dateFiltersContent,
    fromDateValue,
    toDateValue,
    isDefaultDateValue: !fromDateValue && !toDateValue,
    resetDateFilters,
  };
};
